/**
 * chain-eos/avatar.ts — the on-chain avatar choice (`properavatar` contract).
 *
 * The contract stores one row per account: which AtomicAssets asset the account
 * picked as its avatar. That is all. It is deliberately dumb, which is what makes
 * it useful — the wallet, the explorer and anything else read the same row.
 *
 * IMPORTANT: the contract does not verify ownership, and cannot meaningfully do
 * so (people sell and burn NFTs after setting them). Always confirm the account
 * still owns the asset — `loadNftsByIds` returns `owner` for exactly this — before
 * showing the image to other people.
 */

import { Name, UInt64 } from '@wharfkit/antelope'
import { EOS_RPC_ENDPOINTS } from './config.js'
import { fetchRpc } from './rpc-fetch.js'
import type { Session } from './session.js'

export const AVATAR_CONTRACT = 'properavatar'

export interface AvatarChoice {
  account: string
  assetId: string
  /** ISO timestamp of the last change. */
  updated: string
}

interface AvatarRow {
  account: string
  asset_id: string | number
  updated: string
}

/** Reads one account's choice. Returns null when the account never set one. */
export async function fetchAvatarChoice(account: string): Promise<AvatarChoice | null> {
  const rows = await fetchAvatarChoices([account])
  return rows.get(account) ?? null
}

/**
 * Reads several accounts at once — one request per account is unavoidable with
 * `get_table_rows` bounds, so this is capped to keep list views sane.
 */
export async function fetchAvatarChoices(
  accounts: string[],
  options: { limit?: number } = {},
): Promise<Map<string, AvatarChoice>> {
  const unique = [...new Set(accounts.map(a => a.trim()).filter(Boolean))].slice(
    0,
    options.limit ?? 25,
  )
  const out = new Map<string, AvatarChoice>()
  if (unique.length === 0) return out

  await Promise.all(
    unique.map(async account => {
      for (const url of EOS_RPC_ENDPOINTS) {
        try {
          const res = await fetchRpc(url, '/v1/chain/get_table_rows', {
            json: true,
            code: AVATAR_CONTRACT,
            scope: AVATAR_CONTRACT,
            table: 'avatars',
            lower_bound: account,
            upper_bound: account,
            limit: 1,
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const body = (await res.json()) as { rows?: AvatarRow[] }
          const row = body.rows?.[0]
          if (row && row.account === account) {
            out.set(account, {
              account,
              assetId: String(row.asset_id),
              updated: row.updated,
            })
          }
          return
        } catch {
          // try the next endpoint
        }
      }
    }),
  )
  return out
}

/** Publishes the choice. The account pays ~112 bytes of RAM the first time. */
export async function setAvatarOnChain(
  session: Session,
  account: string,
  assetId: string,
): Promise<string> {
  const result = await session.transact({
    action: {
      account: AVATAR_CONTRACT,
      name: 'setavatar',
      authorization: [{ actor: account, permission: 'active' }],
      data: { account: Name.from(account), asset_id: UInt64.from(assetId) },
    },
  })
  return String((result.response as { transaction_id?: string })?.transaction_id ?? '')
}

/** Deletes the account's row and refunds its RAM — the "forget me" action. */
export async function clearAvatarOnChain(session: Session, account: string): Promise<string> {
  const result = await session.transact({
    action: {
      account: AVATAR_CONTRACT,
      name: 'clear',
      authorization: [{ actor: account, permission: 'active' }],
      data: { account: Name.from(account) },
    },
  })
  return String((result.response as { transaction_id?: string })?.transaction_id ?? '')
}
